/**
 * CommentService.js
 * Servicio para gestión de comentarios con funcionalidades avanzadas y notificaciones
 * Incluye todos los métodos existentes + nuevos métodos para notificaciones
 */

import apiClient from './apiClient';

class CommentService {
    // ============================================================================
    // MÉTODOS EXISTENTES PARA COMENTARIOS
    // ============================================================================

    /**
     * Obtener comentarios de una tarea
     */
    async getTaskComments(taskId) {
        if (!taskId) {
            return { success: false, error: 'ID de tarea requerido' };
        }

        try {
            console.log('🔄 Obteniendo comentarios para tarea:', taskId);

            const result = await apiClient.get('task_comments', { task_id: taskId });
            console.log('📥 Respuesta de comentarios:', result);

            if (result.status === 'success') {
                return {
                    success: true,
                    data: result.data || [],
                    message: result.message
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al obtener comentarios'
                };
            }

        } catch (error) {
            console.error('❌ Error obteniendo comentarios:', error);
            return {
                success: false,
                error: `Error de conexión: ${error.message}`
            };
        }
    }

    /**
     * Crear nuevo comentario
     */
    async createComment(commentData) {
        try {
            console.log('🔄 Creando comentario:', commentData);

            const result = await apiClient.post('comments', commentData);
            console.log('📥 Respuesta de creación:', result);

            if (result.status === 'success') {
                return {
                    success: true,
                    data: result.data,
                    message: result.message
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al crear comentario'
                };
            }

        } catch (error) {
            console.error('❌ Error creando comentario:', error);
            return {
                success: false,
                error: `Error de conexión: ${error.message}`
            };
        }
    }

    /**
     * Actualizar comentario existente
     */
    async updateComment(commentId, commentData) {
        try {
            console.log('🔄 Actualizando comentario:', commentId, commentData);

            const result = await apiClient.put(`comments&id=${commentId}`, commentData);
            console.log('📥 Respuesta de actualización:', result);

            if (result.status === 'success') {
                return {
                    success: true,
                    data: result.data,
                    message: result.message
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al actualizar comentario'
                };
            }

        } catch (error) {
            console.error('❌ Error actualizando comentario:', error);
            return {
                success: false,
                error: `Error de conexión: ${error.message}`
            };
        }
    }

    /**
     * Eliminar comentario
     */
    async deleteComment(commentId, userId) {
        try {
            console.log('🔄 Eliminando comentario:', commentId, 'Usuario:', userId);

            const result = await apiClient.del(`comments&id=${commentId}&user_id=${userId}`);
            console.log('📥 Respuesta de eliminación:', result);

            if (result.status === 'success') {
                return {
                    success: true,
                    data: result.data,
                    message: result.message
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al eliminar comentario'
                };
            }

        } catch (error) {
            console.error('❌ Error eliminando comentario:', error);
            return {
                success: false,
                error: `Error de conexión: ${error.message}`
            };
        }
    }

    /**
     * Toggle like en comentario
     */
    async toggleLike(commentId, userId) {
        try {
            console.log('🔄 Toggle like:', commentId, 'Usuario:', userId);

            const result = await apiClient.post('comment_like', {
                comment_id: commentId,
                usuario_id: userId
            });
            console.log('📥 Respuesta de like:', result);

            if (result.status === 'success') {
                return {
                    success: true,
                    data: result.data,
                    message: result.message
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al procesar like'
                };
            }

        } catch (error) {
            console.error('❌ Error procesando like:', error);
            return {
                success: false,
                error: `Error de conexión: ${error.message}`
            };
        }
    }

    /**
     * Obtener estadísticas de comentarios de una tarea
     */
    async getCommentStats(taskId) {
        try {
            console.log('🔄 Obteniendo estadísticas para tarea:', taskId);

            const result = await apiClient.get('comment_stats', { task_id: taskId });
            console.log('📥 Estadísticas obtenidas:', result);

            if (result.status === 'success') {
                return {
                    success: true,
                    data: result.data,
                    message: result.message
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al obtener estadísticas'
                };
            }

        } catch (error) {
            console.error('❌ Error obteniendo estadísticas:', error);
            return {
                success: false,
                error: `Error de conexión: ${error.message}`
            };
        }
    }

    // ============================================================================
    // NUEVOS MÉTODOS PARA NOTIFICACIONES
    // ============================================================================

    /**
     * Obtener resumen de notificaciones para el header
     */
    async getNotificationSummary(userId = 1) {
        try {
            console.log('🔔 Obteniendo resumen de notificaciones para usuario:', userId);

            const result = await apiClient.get('notification_summary', { user_id: userId });
            console.log('📥 Resumen de notificaciones:', result);

            if (result.status === 'success') {
                return {
                    success: true,
                    data: result.data,
                    message: result.message
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al obtener resumen'
                };
            }

        } catch (error) {
            console.error('❌ Error obteniendo resumen de notificaciones:', error);
            return {
                success: false,
                error: `Error de conexión: ${error.message}`,
                data: {
                    total_unread: 0,
                    tasks_with_comments: 0,
                    has_notifications: false
                }
            };
        }
    }

    /**
     * Obtener tareas con comentarios nuevos (para dropdown)
     */
    async getTasksWithNewComments(userId = 1, limit = 10) {
        try {
            console.log('🔔 Obteniendo tareas con comentarios nuevos:', { userId, limit });

            const result = await apiClient.get('tasks_with_new_comments', { user_id: userId, limit });
            console.log('📥 Tareas con comentarios nuevos:', result);

            if (result.status === 'success') {
                return {
                    success: true,
                    data: result.data || [],
                    message: result.message
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al obtener tareas'
                };
            }

        } catch (error) {
            console.error('❌ Error obteniendo tareas con comentarios:', error);
            return {
                success: false,
                error: `Error de conexión: ${error.message}`,
                data: []
            };
        }
    }

    /**
     * Marcar comentarios de una tarea como leídos
     */
    async markTaskCommentsAsRead(taskId, userId = 1) {
        try {
            console.log('✅ Marcando comentarios como leídos:', { taskId, userId });

            const result = await apiClient.post('mark_comments_read', {
                task_id: taskId,
                user_id: userId
            });
            console.log('📥 Comentarios marcados como leídos:', result);

            if (result.status === 'success') {
                return {
                    success: true,
                    data: result.data,
                    message: result.message
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al marcar comentarios'
                };
            }

        } catch (error) {
            console.error('❌ Error marcando comentarios como leídos:', error);
            return {
                success: false,
                error: `Error de conexión: ${error.message}`
            };
        }
    }

    /**
     * Marcar todas las notificaciones como leídas
     */
    async markAllNotificationsAsRead(userId = 1) {
        try {
            console.log('✅ Marcando todas las notificaciones como leídas para usuario:', userId);

            const result = await apiClient.post('mark_all_notifications_read', {
                user_id: userId
            });
            console.log('📥 Todas las notificaciones marcadas:', result);

            if (result.status === 'success') {
                return {
                    success: true,
                    data: result.data,
                    message: result.message
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al marcar notificaciones'
                };
            }

        } catch (error) {
            console.error('❌ Error marcando todas las notificaciones:', error);
            return {
                success: false,
                error: `Error de conexión: ${error.message}`
            };
        }
    }

    /**
     * Obtener comentarios no leídos
     */
    async getUnreadComments(userId = 1) {
        try {
            console.log('🔔 Obteniendo comentarios no leídos para usuario:', userId);

            const result = await apiClient.get('unread_comments', { user_id: userId });
            console.log('📥 Comentarios no leídos:', result);

            if (result.status === 'success') {
                return {
                    success: true,
                    data: result.data || [],
                    message: result.message
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al obtener comentarios'
                };
            }

        } catch (error) {
            console.error('❌ Error obteniendo comentarios no leídos:', error);
            return {
                success: false,
                error: `Error de conexión: ${error.message}`,
                data: []
            };
        }
    }

    // ============================================================================
    // MÉTODOS DE UTILIDAD Y FORMATEO
    // ============================================================================

    /**
     * Formatear comentarios para el frontend
     */
    formatCommentsForFrontend(comments) {
        if (!Array.isArray(comments)) {
            console.warn('⚠️ Comments no es un array:', comments);
            return [];
        }

        return comments.map(comment => ({
            id: comment.id,
            taskId: comment.tarea_id,
            userId: comment.usuario_id,
            autor: comment.usuario_nombre || 'Usuario Anónimo',
            autorAvatar: comment.autor_avatar || null,
            contenido: comment.contenido || '',
            texto: comment.contenido || '', // Alias para compatibilidad
            parentId: comment.parent_id,
            likesCount: parseInt(comment.likes_count) || 0,
            likedBy: Array.isArray(comment.liked_by) ? comment.liked_by : [],
            likedByNames: Array.isArray(comment.liked_by_names) ? comment.liked_by_names : [],
            // Personas (id, nombre, avatar) para el popover del corazón. Si el backend
            // aún no envía liked_by_people (pre-deploy), se derivan de los nombres con
            // avatar nulo → el Avatar cae a iniciales; las fotos llegan al desplegar.
            likedByPeople: Array.isArray(comment.liked_by_people)
              ? comment.liked_by_people
              : (Array.isArray(comment.liked_by_names)
                  ? comment.liked_by_names.map((n, i) => ({ id: `n${i}`, nombre: n, avatar: null }))
                  : []),
            isLiked: false, // Se actualiza dinámicamente en el frontend
            estado: comment.estado || 'activo',
            editado: Boolean(comment.editado),
            fecha: comment.fecha_creacion,
            fechaCreacion: comment.fecha_creacion, // Alias para compatibilidad
            fechaActualizacion: comment.fecha_actualizacion,
            metadata: comment.metadata || {},
            // Lista plana cronológica: ya no se anidan respuestas (el hilo se
            // sigue con menciones @usuario). El backend devuelve todo al mismo
            // nivel; mantenemos respuestas:[] por compatibilidad del render.
            respuestas: []
        }));
    }

    /**
     * Formatear un comentario individual
     */
    formatSingleComment(comment) {
        return {
            id: comment.id,
            taskId: comment.tarea_id,
            userId: comment.usuario_id,
            autor: comment.usuario_nombre || 'Usuario Anónimo',
            autorAvatar: comment.autor_avatar || null,
            contenido: comment.contenido || '',
            texto: comment.contenido || '',
            parentId: comment.parent_id,
            likesCount: parseInt(comment.likes_count) || 0,
            likedBy: Array.isArray(comment.liked_by) ? comment.liked_by : [],
            likedByNames: Array.isArray(comment.liked_by_names) ? comment.liked_by_names : [],
            // Personas (id, nombre, avatar) para el popover del corazón. Si el backend
            // aún no envía liked_by_people (pre-deploy), se derivan de los nombres con
            // avatar nulo → el Avatar cae a iniciales; las fotos llegan al desplegar.
            likedByPeople: Array.isArray(comment.liked_by_people)
              ? comment.liked_by_people
              : (Array.isArray(comment.liked_by_names)
                  ? comment.liked_by_names.map((n, i) => ({ id: `n${i}`, nombre: n, avatar: null }))
                  : []),
            isLiked: false,
            estado: comment.estado || 'activo',
            editado: Boolean(comment.editado),
            fecha: comment.fecha_creacion,
            fechaCreacion: comment.fecha_creacion,
            fechaActualizacion: comment.fecha_actualizacion,
            metadata: comment.metadata || {},
            respuestas: []
        };
    }

    /**
     * Formatear tareas con comentarios para notificaciones
     */
    formatTasksForNotifications(tasks) {
        if (!Array.isArray(tasks)) {
            return [];
        }

        return tasks.map(task => ({
            id: task.taskId,
            taskId: task.taskId,
            name: task.taskName,
            taskName: task.taskName,
            status: task.taskStatus,
            priority: task.taskPriority,
            newCommentsCount: task.newCommentsCount,
            latestCommentDate: task.latestCommentDate,
            lastCommenter: task.lastCommenter,
            lastCommentPreview: task.lastCommentPreview,
            // Formatear fecha para display
            timeAgo: this.getTimeAgo(task.latestCommentDate)
        }));
    }

    /**
     * Obtener tiempo transcurrido en formato legible
     */
    getTimeAgo(dateString) {
        if (!dateString) return 'Fecha desconocida';

        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffInSeconds = Math.floor((now - date) / 1000);

            if (diffInSeconds < 60) {
                return 'Hace un momento';
            } else if (diffInSeconds < 3600) {
                const minutes = Math.floor(diffInSeconds / 60);
                return `Hace ${minutes} minuto${minutes !== 1 ? 's' : ''}`;
            } else if (diffInSeconds < 86400) {
                const hours = Math.floor(diffInSeconds / 3600);
                return `Hace ${hours} hora${hours !== 1 ? 's' : ''}`;
            } else if (diffInSeconds < 2592000) {
                const days = Math.floor(diffInSeconds / 86400);
                return `Hace ${days} día${days !== 1 ? 's' : ''}`;
            } else {
                return date.toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: 'short',
                    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                });
            }
        } catch (error) {
            console.error('Error formateando fecha:', error);
            return 'Fecha inválida';
        }
    }

    /**
     * Validar datos de comentario antes de enviar
     */
    validateCommentData(data) {
        const errors = [];

        if (!data.tarea_id) {
            errors.push('ID de tarea es requerido');
        }

        if (!data.usuario_id) {
            errors.push('ID de usuario es requerido');
        }

        if (!data.contenido || !data.contenido.trim()) {
            errors.push('Contenido del comentario es requerido');
        }

        if (data.contenido && data.contenido.length > 2000) {
            errors.push('El comentario es demasiado largo (máximo 2000 caracteres)');
        }

        return {
            isValid: errors.length === 0,
            errors: errors
        };
    }

    /**
     * Limpiar HTML de contenido (para prevenir XSS)
     */
    sanitizeContent(content) {
        if (!content) return '';
        
        // Remover scripts y contenido peligroso
        return content
            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
            .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
            .replace(/javascript:/gi, '')
            .replace(/on\w+="/gi, 'data-removed="')
            .trim();
    }
}

// Crear instancia singleton
const commentService = new CommentService();

export default commentService;